import { useState, useCallback } from 'react';
import { useChain } from '@cosmos-kit/react';
import type { Auction } from '@/types/auctions';
import { createBidMsg, toMicroUnits, fromMicroUnits } from '@/services/auction-messages';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { text } from '@/config/theme';

interface BidModalProps {
  auction: Auction;
  minBidFee: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BidModal({
  auction,
  minBidFee,
  onClose,
  onSuccess,
}: BidModalProps): JSX.Element {
  const [bidAmount, setBidAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [acknowledgedNonCancelable, setAcknowledgedNonCancelable] = useState(false);

  const { address, getSigningStargateClient, isWalletConnected } = useChain(
    'gravitybridge'
  );

  const currentBid = auction.highest_bid !== null && auction.highest_bid !== undefined
    ? parseFloat(fromMicroUnits(auction.highest_bid.bid_amount))
    : 0;

  const isHighestBidder = auction.highest_bid?.bidder_address === address;

  const totalCost = bidAmount !== '' && !isNaN(parseFloat(bidAmount))
    ? parseFloat(bidAmount) + parseFloat(fromMicroUnits(minBidFee))
    : parseFloat(fromMicroUnits(minBidFee));

  const handleSubmitBid = async (): Promise<void> => {
    if (address === undefined || address === '' || !isWalletConnected) {
      setError('Please connect your wallet');
      return;
    }

    if (isHighestBidder) {
      setError('You are already the highest bidder');
      return;
    }

    if (bidAmount === '' || isNaN(parseFloat(bidAmount)) || parseFloat(bidAmount) <= 0) {
      setError('Please enter a valid bid amount');
      return;
    }

    const bidValue = parseFloat(bidAmount);
    if (bidValue <= currentBid) {
      setError(`Bid must be higher than current bid (${currentBid.toFixed(6)} GRAV)`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setTxHash(null);

    try {
      const client = await getSigningStargateClient();

      const bidMsg = createBidMsg({
        auction_id: auction.id,
        bidder: address,
        bid_amount: toMicroUnits(bidAmount),
        bid_fee: minBidFee,
      });

      const fee = {
        amount: [{ denom: 'ugraviton', amount: '10000' }],
        gas: '300000',
      };

      const result = await client.signAndBroadcast(
        address,
        [bidMsg],
        fee,
        ''
      );

      if (result.code !== 0) {
        throw new Error(`Transaction failed: ${result.rawLog ?? 'Unknown error'}`);
      }

      setTxHash(result.transactionHash);
      onSuccess?.();
    } catch (err) {
      console.error('Bid error:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit bid');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback((): void => {
    if (!isSubmitting) {
      setBidAmount('');
      setError(null);
      setTxHash(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  return (
    <Modal isOpen={true} onClose={handleClose} title={`Bid on Auction #${auction.id}`}>
      <div className="space-y-4">
        {/* Auction Details */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className={text.secondary}>Token:</span>
            <span className={`font-medium ${text.primary}`}>
              {auction.display?.token_symbol ?? (auction.amount?.denom === 'ugraviton' ? 'GRAV' : auction.amount?.denom ?? 'N/A')}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className={text.secondary}>Amount:</span>
            <span className={`font-medium ${text.primary}`}>
              {auction.display?.amount_display ?? (auction.amount !== null && auction.amount !== undefined ? fromMicroUnits(auction.amount.amount) : 'N/A')}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className={text.secondary}>Current Highest Bid:</span>
            <span className={`font-medium ${text.primary}`}>
              {currentBid > 0 ? `${currentBid.toFixed(6)} GRAV` : 'No bids yet'}
            </span>
          </div>
          {auction.highest_bid !== null && auction.highest_bid !== undefined && (
            <div className="flex justify-between text-sm">
              <span className={text.secondary}>Highest Bidder:</span>
              <span className={`font-mono text-xs ${text.primary}`}>
                {auction.highest_bid.bidder_address.slice(0, 10)}...{auction.highest_bid.bidder_address.slice(-8)}
              </span>
            </div>
          )}
        </div>

        {/* Warning if user is highest bidder */}
        {isHighestBidder && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className={`text-sm ${text.warning}`}>
              You are already the highest bidder on this auction.
            </p>
          </div>
        )}

        {/* Bid Amount Input */}
        <div>
          <label htmlFor="bid-amount-input" className={`block text-sm font-medium ${text.secondary} mb-1`}>
            Your Bid Amount (GRAV)
          </label>
          <input
            id="bid-amount-input"
            type="number"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            placeholder={`Enter amount (min: ${(currentBid + 0.000001).toFixed(6)})`}
            disabled={isSubmitting || txHash !== null}
            step="0.000001"
            min="0"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Fee Information */}
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="text-sm">
              <p className={`font-medium ${text.warning}`}>Bid Fee (Non-refundable)</p>
              <p className={`${text.warning} mt-1`}>
                {fromMicroUnits(minBidFee)} GRAV fee required for every bid
              </p>
              <p className={`font-medium ${text.primary} mt-2`}>
                Total Cost: {totalCost.toFixed(6)} GRAV
              </p>
            </div>
          </div>
        </div>

        {/* Acknowledgment Checkbox */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="acknowledge-non-cancelable"
            checked={acknowledgedNonCancelable}
            onChange={(e) => { setAcknowledgedNonCancelable(e.target.checked); }}
            disabled={isSubmitting || txHash !== null}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="acknowledge-non-cancelable" className={`text-sm ${text.secondary}`}>
            I understand that bids cannot be canceled once placed
          </label>
        </div>

        {/* Error Message */}
        {error !== null && error !== '' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className={`text-sm ${text.error}`}>{error}</p>
          </div>
        )}

        {/* Success Message */}
        {txHash !== null && txHash !== '' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className={`text-sm ${text.success} font-medium`}>Bid submitted successfully!</p>
            <p className={`text-xs ${text.success} mt-1 font-mono break-all`}>
              Tx: {txHash}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={handleClose}
            variant="secondary"
            className="flex-1"
            disabled={isSubmitting}
          >
            {txHash !== null ? 'Close' : 'Cancel'}
          </Button>
          {txHash === null && (
            <Button
              onClick={() => { void handleSubmitBid(); }}
              variant="primary"
              className="flex-1"
              disabled={isSubmitting || !isWalletConnected || isHighestBidder || !acknowledgedNonCancelable}
              isLoading={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Bid'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
